import { Component, createRef } from "react";
import { createPortal } from "react-dom";
import { RemoveScroll } from "react-remove-scroll";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { iosSetBackgroundColor, isIOSApp } from "../../tools/ios";
import AppCarousel, { type AppCarouselHandle } from "../app/AppCarousel";
import ResponsiveImage, {
  type ResponsiveImageSizes,
} from "../app/ResponsiveImage";
import CustomIcon from "../CustomIcon";
import { cn } from "../../lib/utils";
import LegendText from "./LegendText";

interface ImageItem {
  url: string;
  path?: string;
  name?: string | null;
  legend?: string;
  sizes?: ResponsiveImageSizes | null;
}

interface ImageViewerProps {
  open: boolean;
  imageIndex: number;
  imgs: ImageItem[];
  onClose: (e: React.MouseEvent) => void;
  updateIndex: (index: number) => void;
}

interface ImageViewerState {
  preventSwipe: boolean;
  captionVisible: boolean;
  currentScale: number;
  captionClamped: boolean;
  // Bumped to remount the slides, which drops every figure's zoom.
  zoomResetCount: number;
}

class ImageViewer extends Component<ImageViewerProps, ImageViewerState> {
  override state: ImageViewerState = {
    preventSwipe: false,
    captionVisible: true,
    currentScale: 1,
    captionClamped: true,
    zoomResetCount: 0,
  };

  private carouselRef = createRef<AppCarouselHandle>();

  override render() {
    const slideHeight = Math.round(window.innerHeight * 0.6);
    const slideTranslate = Math.round(window.innerHeight * 0.45);
    const captionBottom =
      this.state.captionVisible || !this.state.captionClamped
        ? 0
        : -Math.round(window.innerHeight * 0.18);

    return createPortal(
      <div
        className={cn(
          "image-viewer-wrapper w-full bg-[rgba(0,0,0,0.9)] backdrop-blur-xs z-900 p-0 fixed overflow-hidden inset-0 -mt-px",
          this.props.open ? "block" : "hidden"
        )}
        style={{ height: "calc(100% + 1px)" }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <style>{`
          .image-viewer-slide img {
            display: block;
            max-width: 90%;
            max-height: ${slideHeight}px;
            object-fit: contain;
            margin: 0 auto;
          }
          .image-viewer-slide .responsive-img-fallback {
            position: absolute;
            display: block;
            max-width: 90%;
            margin: 0 auto;
            inset: 0;
          }
        `}</style>

        <RemoveScroll enabled={false}>
          <div
            onClick={(e) => {
              this.props.onClose(e);
              this.setState({
                captionVisible: true,
                captionClamped: true,
                currentScale: 1,
              });
              if (isIOSApp()) iosSetBackgroundColor("#FFFFFF");
            }}
          >
            <CustomIcon
              style={{
                position: "absolute",
                top: window.innerWidth >= 768 ? 24 : window.innerHeight * 0.1,
                right: "40px",
                zIndex: 999,
              }}
              iconName="close_alt"
            />
          </div>
          {/* NOTE: Mount on open so the carousel starts at the opened image.
              Each figure zooms on its own: no transformed ancestor sits above
              the horizontal scroller, which WKWebView may not pan. */}
          {this.props.open && (
            <div className="w-screen">
              <AppCarousel
                ref={this.carouselRef}
                slideGutter="sm"
                initialSlide={this.props.imageIndex}
                swipeable={this.state.currentScale <= 1}
                displayPagination={false}
                onIndexChange={(index) => {
                  this.props.updateIndex(index);
                }}
              >
                {this.props.imgs.map((img) => (
                  <div
                    key={`${img.url}-${this.state.zoomResetCount}`}
                    className="image-viewer-slide relative"
                    style={{
                      maxHeight: slideHeight,
                      marginTop: slideHeight,
                      transform: `translateY(-${slideTranslate}px)`,
                    }}
                    onClick={() => {
                      if (this.state.captionClamped) {
                        this.setState({
                          captionVisible: !this.state.captionVisible,
                        });
                      }
                    }}
                  >
                    <TransformWrapper
                      initialScale={1}
                      minScale={1}
                      onZoomStart={() => {
                        this.setState({
                          preventSwipe: true,
                        });
                      }}
                      onZoomStop={({ state }) => {
                        this.setState({
                          currentScale: state.scale,
                          preventSwipe: false,
                        });
                      }}
                      pinch={{ step: 100 }}
                      panning={{ disabled: this.state.currentScale <= 1 }}
                    >
                      <TransformComponent
                        wrapperClass="w-full!"
                        contentClass="w-full!"
                      >
                        <ResponsiveImage
                          image={{
                            url: img.url,
                            path: img.path ?? "",
                            name: img.name,
                            legend: img.legend,
                            sizes: img.sizes,
                          }}
                          alt={img.legend ?? ""}
                          sizes="90vw"
                          loading="eager"
                        />
                      </TransformComponent>
                    </TransformWrapper>
                  </div>
                ))}
              </AppCarousel>
            </div>
          )}

          <div
            className="absolute inset-x-0 bg-white p-[30px_30px_60px] rounded-t-2xl max-h-[70%] md:max-h-none overflow-y-scroll md:overflow-y-auto transition-[bottom] ease-in-out duration-300 [&_p]:text-black [&_p]:font-dm-sans [&_p]:text-sm/5 [&_p]:m-0 [&_p.title]:mb-[10px] [&_p.title]:font-bold [&_.indicators]:block [&_.indicators]:mx-auto [&_.indicators]:text-center [&_.caption-text]:max-h-[50vh] md:[&_.caption-text]:max-h-none [&_.caption-text]:overflow-scroll md:[&_.caption-text]:overflow-visible [&_.caption-text]:[display:-webkit-box] md:[&_.caption-text]:block [&_.caption-text]:[-webkit-box-orient:vertical] [&_.caption-text]:[-webkit-line-clamp:100] md:[&_.caption-text]:[-webkit-line-clamp:unset] [&_.caption-text.lineclamp]:[-webkit-line-clamp:2] md:[&_.caption-text.lineclamp]:[-webkit-line-clamp:unset]"
            style={{ bottom: captionBottom }}
            onClick={() => {
              this.setState({
                captionClamped: !this.state.captionClamped,
              });
            }}
          >
            <div className="flex items-center justify-center gap-1 pb-3">
              {this.props.imgs.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  aria-label={`${index + 1} / ${this.props.imgs.length}`}
                  aria-current={this.props.imageIndex === index}
                  className={cn(
                    "cursor-pointer border-0 p-0 rounded-full",
                    this.props.imageIndex === index
                      ? "h-2 w-8 bg-black"
                      : "size-2 bg-black/30"
                  )}
                  onClick={(e) => {
                    // A dot moves the carousel; it does not toggle the caption.
                    e.stopPropagation();
                    // A zoomed figure blocks the swipe: a dot click leaves
                    // every figure at its normal size.
                    this.setState({
                      currentScale: 1,
                      zoomResetCount: this.state.zoomResetCount + 1,
                    });
                    const carousel = this.carouselRef.current;
                    if (carousel === null)
                      throw new Error(
                        "should not happen: viewer carousel is not mounted"
                      );
                    carousel.scrollToIndex(index, "smooth");
                  }}
                />
              ))}
            </div>
            <p
              className={`caption-text ${this.state.captionClamped ? "lineclamp" : ""}`}
            >
              <LegendText>
                {this.props.imgs.at(this.props.imageIndex)?.legend ?? ""}
              </LegendText>
            </p>
          </div>
        </RemoveScroll>
      </div>,
      document.body
    );
  }
}

export default ImageViewer;
